import { Suspense } from 'react'
import { useMatches } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { trpc } from '@/main'
import { LogoSkeleton, NavSkeleton, UserMenuSkeleton } from './app-layout-skeleton'
import { AppSectionNav } from './app-section-nav'
import { UserMenu } from './user-menu'
import CompareButton from './shared/compare-button'
import { Logo } from './shared/logo'
import { useIsLoggingOut } from '@/lib/logout-signal'
import { isAnonymousComparableRoute } from '@/route-access'

function AppTopBarContent() {
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const loggingOut = useIsLoggingOut()
  const matches = useMatches()
  const routeId = matches[matches.length - 1]?.routeId ?? ''
  // The anonymous "compare my profile" CTA only makes sense on the public market / public-profile pages.
  // On any other route a null user is an expired session mid-redirect, not a genuine anonymous visitor.
  const showCompareCTA = isAnonymousComparableRoute(routeId)

  return (
    <>
      {/* The left logo is ALWAYS the D'Anchiano wordmark, regardless of role or company view (the
              platform standard, like Sesame/Factorial). The active company is surfaced in the user menu,
              not the topbar. It renders full-width at every breakpoint: below `md` the section nav has moved
              to the bottom bar, so nothing competes with it for the row (it used to be clipped to w-13). */}
      <Logo className="shrink-0" />

      {/* Desktop only — below `md` these same links render in the sticky bottom bar (app-bottom-nav.tsx).
            The nav carries its own `hidden md:flex`, so `ml-auto` below still right-aligns the user menu. */}
      <AppSectionNav variant="topbar" />

      {/* Anonymous visitors on the public market / public-profile pages get the "compare my profile"
            sign-in CTA instead of the user menu, which has nothing to offer them. `ml-auto` keeps the CTA
            right-aligned when the (signed-in only) nav isn't rendered. A null user anywhere ELSE — a
            logout in flight, or an EXPIRED session before the redirect to /login lands — reads `null`
            momentarily; keep a neutral skeleton there instead of flashing the (irrelevant) compare CTA. */}
      <div className="ml-auto">
        {user ? <UserMenu /> : loggingOut || !showCompareCTA ? <UserMenuSkeleton /> : <CompareButton />}
      </div>
    </>
  )
}

function AppTopBarFallback() {
  return (
    <>
      <LogoSkeleton />
      <NavSkeleton />
      <UserMenuSkeleton />
    </>
  )
}

export function AppTopBar() {
  return (
    <header
      data-testid="app-topbar"
      className="max-w-9xl sticky top-0 z-50 flex h-18 w-full shrink-0 items-center justify-center px-2 md:px-4 print:hidden"
    >
      <div className="flex w-full items-center gap-2 md:gap-6">
        <Suspense fallback={<AppTopBarFallback />}>
          <AppTopBarContent />
        </Suspense>
      </div>
    </header>
  )
}
